import { nullishToUndefinedable } from '@cc-shopping/utils/valibot';
import * as v from 'valibot';
import { analyticsPropsSchema, baseOfferImageSchema } from '../../cross-site/marketplace/base';
import {
  EntityPlacementCtaTypeEnum,
  MarketplaceStatusEnum,
} from '../../cross-site/marketplace/enums';
import { structuredContentDocumentSchema } from '../../cross-site/wordpress/schema';
import { ALL_CATEGORIES } from './types';

export const offerSchema = v.object({
  analyticsProps: analyticsPropsSchema,
  annualFee: v.number('offer.annualFee is required'),
  annualFeeDetails: v.string('offer.annualFeeDetails is required'),
  applyLink: v.pipe(
    v.string('offer.applyLink is required'),
    v.trim(),
    v.url('offer.applyLink must be a valid URL'),
  ),
  aprDisplayList: v.array(
    v.string('offer.aprDisplayList[number] must be a string'),
    'offer.aprDisplayList must be an array of strings',
  ),
  compareCategories: v.array(
    v.union(
      ALL_CATEGORIES.map(cat => v.literal(cat)),
      'offer.compareCategories[number] must be a valid category',
    ),
    'offer.compareCategories must be an array of valid categories',
  ),
  consBullets: v.fallback(
    v.array(
      v.string('offer.consBullets[number] must be a string'),
      'offer.consBullets must be an array of strings',
    ),
    [],
  ),
  cta: v.object(
    {
      link: v.string('offer.cta.link is required'),
      type: v.enum(
        EntityPlacementCtaTypeEnum,
        'offer.cta.type must be a valid EntityPlacementCtaTypeEnum value',
      ),
    },
    'offer.cta must be a valid object',
  ),
  displayName: nullishToUndefinedable(v.string('offer.displayName must be a string when provided')),
  driverIntroApr: nullishToUndefinedable(
    v.object(
      {
        label: v.string('offer.driverIntroApr.label is required'),
        value: nullishToUndefinedable(
          v.string('offer.driverIntroApr.value must be a string when provided'),
        ),
      },
      'offer.driverIntroApr must be a valid driver object',
    ),
  ),
  id: v.string('offer.id is required'),
  image: baseOfferImageSchema,
  institution: v.object(
    {
      name: v.string('offer.institution.name is required'),
      id: v.string('offer.institution.id is required'),
    },
    'offer.institution must be a valid institution object',
  ),
  legacyId: v.number('offer.legacyId is required'),
  limitedCreditHistory: v.boolean('offer.limitedCreditHistory is required'),
  maxFico: v.number('offer.maxFico is required'),
  minFico: v.number('offer.minFico is required'),
  monetizable: v.picklist(
    ['yes_assumed', 'no', 'no_because_of_limit'],
    'offer.monetizable must be one of: yes_assumed, no, no_because_of_limit',
  ),
  name: v.string('offer.name is required'),
  phoneNumber: nullishToUndefinedable(v.string('offer.phoneNumber must be a string when provided')),
  product: v.object(
    {
      name: v.string('offer.product.name is required'),
      id: v.string('offer.product.id is required'),
    },
    'offer.product must be a valid object',
  ),
  productType: v.string('offer.productType is required'),
  prosBullets: v.fallback(
    v.array(
      v.string('offer.prosBullets[number] must be a string'),
      'offer.prosBullets must be an array of strings',
    ),
    [],
  ),
  reviewLink: v.pipe(
    v.string('offer.reviewLink is required'),
    v.trim(),
    v.url('offer.reviewLink must be a valid URL'),
  ),
  rewardRateNotes: nullishToUndefinedable(
    v.string('offer.rewardRateNotes must be a string when provided'),
  ),
  signUpBonusDetails: v.string('offer.signUpBonusDetails is required'),
  starRating: v.fallback(v.number('offer.starRating must be a number when provided'), 0),
  starRatingTooltip: nullishToUndefinedable(
    v.string('offer.starRatingTooltip must be a string when provided'),
  ),
  status: v.pipe(
    v.string('offer.status is required'),
    v.toUpperCase(),
    v.enum(MarketplaceStatusEnum, 'offer.status must be a valid MarketplaceStatusEnum value'),
    v.transform(val => val?.toLowerCase()),
  ),
  termsAndFeesLink: nullishToUndefinedable(
    v.string('offer.termsAndFeesLink must be a string when provided'),
  ),
  versionId: v.number('offer.versionId is required'),
});

export const baseCompareSchema = v.object({
  belowSectionsContent: structuredContentDocumentSchema,
  roundupOffers: v.record(
    v.string('baseCompare.roundupOffers key must be a string'),
    v.array(offerSchema, 'baseCompare.roundupOffers must be an array of offers'),
  ),
});
